import { useEffect, useState, useCallback } from "react";
import { Plus, X, Loader2, PackageOpen, Trash2, AlertCircle } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const STATUS_COLOR = {
  OPEN: "bg-[#F4FBE6] text-[#5BA508] border-[#99D420]",
  IN_PROGRESS: "bg-[#FFFAE6] text-[#A77B00] border-[#FFC107]",
  COMPLETED: "bg-[#EBF5FB] text-[#006E9E] border-[#006E9E]",
  EXPIRED: "bg-gray-100 text-gray-600 border-gray-400",
};

const ISO_TYPES = [
  { v: "22G1", l: "20DV (20' Standard)" },
  { v: "25G1", l: "20HC (20' High Cube)" },
  { v: "22U1", l: "20OT (20' Open Top)" },
  { v: "42G1", l: "40DV (40' Standard)" },
  { v: "45G1", l: "40HC (40' High Cube)" },
  { v: "42U1", l: "40OT (40' Open Top)" },
  { v: "22R1", l: "20RF (20' Reefer)" },
  { v: "45R1", l: "40RH (40' High-Cube Reefer)" },
];

const empty = () => ({
  carrier_code: "ARK",
  transport_order: "",
  vessel: "",
  gate_open: "",
  gate_close: "",
  notes: "",
  bookings: [
    {
      booking_no: "",
      destination: "",
      notes: "",
      lines: [{ iso_type: "22G1", qty: 1 }],
    },
  ],
});

function CreateDialog({ onClose, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const upd = (path, val) => {
    setForm((prev) => {
      const out = { ...prev };
      const parts = path.split(".");
      let cur = out;
      for (let i = 0; i < parts.length - 1; i++) {
        const k = parts[i].includes("[") ? parts[i].match(/(\w+)\[(\d+)\]/) : null;
        if (k) {
          cur[k[1]] = [...cur[k[1]]];
          cur[k[1]][+k[2]] = { ...cur[k[1]][+k[2]] };
          cur = cur[k[1]][+k[2]];
        } else {
          cur[parts[i]] = { ...cur[parts[i]] };
          cur = cur[parts[i]];
        }
      }
      cur[parts[parts.length - 1]] = val;
      return out;
    });
  };

  const addBooking = () => setForm((p) => ({
    ...p,
    bookings: [...p.bookings, { booking_no: "", destination: "", notes: "", lines: [{ iso_type: "22G1", qty: 1 }] }],
  }));
  const removeBooking = (i) => setForm((p) => ({ ...p, bookings: p.bookings.filter((_, idx) => idx !== i) }));

  const addLine = (bi) => setForm((p) => {
    const n = { ...p, bookings: [...p.bookings] };
    n.bookings[bi] = { ...n.bookings[bi], lines: [...n.bookings[bi].lines, { iso_type: "22G1", qty: 1 }] };
    return n;
  });
  const removeLine = (bi, li) => setForm((p) => {
    const n = { ...p, bookings: [...p.bookings] };
    n.bookings[bi] = { ...n.bookings[bi], lines: n.bookings[bi].lines.filter((_, idx) => idx !== li) };
    return n;
  });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.bookings.length || form.bookings.some((b) => !b.booking_no.trim() || !b.lines.length || b.lines.some((l) => !l.iso_type || +l.qty < 1))) {
      setErr("Every booking needs a number and at least one valid line (ISO type + qty ≥ 1).");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        carrier_code: form.carrier_code.trim().toUpperCase(),
        gate_open: form.gate_open ? new Date(form.gate_open).toISOString() : null,
        gate_close: form.gate_close ? new Date(form.gate_close).toISOString() : null,
        bookings: form.bookings.map((b) => ({
          ...b,
          booking_no: b.booking_no.trim().toUpperCase(),
          lines: b.lines.map((l) => ({ iso_type: l.iso_type, qty: +l.qty })),
        })),
      };
      const { data } = await api.post("/release-orders", payload);
      toast.success(`Release order created — ${data.total_qty} container slot(s) open.`);
      onSaved && onSaved(data);
      onClose();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="ro-create-dialog">
      <div className="bg-white border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#006E9E]">New release order</div>
            <h2 className="text-lg font-bold mt-0.5">Carrier-issued gate-OUT authorisation</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100" data-testid="ro-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Carrier *</label>
              <input value={form.carrier_code} onChange={(e) => upd("carrier_code", e.target.value.toUpperCase())} className="ps-input font-mono" required data-testid="ro-carrier" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Transport order #</label>
              <input value={form.transport_order} onChange={(e) => upd("transport_order", e.target.value)} placeholder="e.g. T2672842" className="ps-input font-mono" data-testid="ro-transport" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Vessel</label>
              <input value={form.vessel} onChange={(e) => upd("vessel", e.target.value)} placeholder="optional" className="ps-input" data-testid="ro-vessel" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Gate opening</label>
              <input type="datetime-local" value={form.gate_open} onChange={(e) => upd("gate_open", e.target.value)} className="ps-input" data-testid="ro-gate-open" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Gate closure</label>
              <input type="datetime-local" value={form.gate_close} onChange={(e) => upd("gate_close", e.target.value)} className="ps-input" data-testid="ro-gate-close" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Notes</label>
              <input value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="ps-input" placeholder="optional" />
            </div>
          </div>

          {form.bookings.map((bk, bi) => (
            <div key={bi} className="border border-gray-200 bg-[#F8F8F8]/60 p-4" data-testid={`ro-booking-${bi}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#006E9E]">Booking #{bi + 1}</div>
                {form.bookings.length > 1 && (
                  <button type="button" onClick={() => removeBooking(bi)} className="text-xs text-[#F1471D] hover:underline" data-testid={`ro-remove-booking-${bi}`}>Remove</button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Booking # *</label>
                  <input value={bk.booking_no} onChange={(e) => upd(`bookings[${bi}].booking_no`, e.target.value.toUpperCase())} className="ps-input font-mono" required data-testid={`ro-booking-no-${bi}`} />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Destination</label>
                  <input value={bk.destination} onChange={(e) => upd(`bookings[${bi}].destination`, e.target.value)} placeholder="e.g. LA SPEZIA (ITSPE)" className="ps-input" data-testid={`ro-dest-${bi}`} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-600">Container lines</div>
                {bk.lines.map((ln, li) => (
                  <div key={li} className="flex items-center gap-2">
                    <input type="number" min="1" value={ln.qty} onChange={(e) => upd(`bookings[${bi}].lines[${li}].qty`, +e.target.value)} className="ps-input w-20 font-mono" data-testid={`ro-qty-${bi}-${li}`} />
                    <span className="text-sm text-gray-500">×</span>
                    <select value={ln.iso_type} onChange={(e) => upd(`bookings[${bi}].lines[${li}].iso_type`, e.target.value)} className="ps-input font-mono flex-1" data-testid={`ro-iso-${bi}-${li}`}>
                      {ISO_TYPES.map((t) => <option key={t.v} value={t.v}>{t.v} — {t.l}</option>)}
                    </select>
                    {bk.lines.length > 1 && (
                      <button type="button" onClick={() => removeLine(bi, li)} className="p-1 text-[#F1471D] hover:bg-red-50" data-testid={`ro-remove-line-${bi}-${li}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => addLine(bi)} className="text-xs text-[#006E9E] hover:underline inline-flex items-center gap-1" data-testid={`ro-add-line-${bi}`}>
                  <Plus className="w-3 h-3" /> Add another container type
                </button>
              </div>
            </div>
          ))}

          <button type="button" onClick={addBooking} className="ps-btn ps-btn-secondary w-full" data-testid="ro-add-booking">
            <Plus className="w-4 h-4" /> Add another booking
          </button>

          {err && <div className="border border-[#F1471D] bg-[#FFF1ED] px-4 py-3 text-[#F1471D] text-sm inline-flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {err}</div>}

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary" disabled={saving}>Cancel</button>
            <button type="submit" className="ps-btn ps-btn-primary" disabled={saving} data-testid="ro-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageOpen className="w-4 h-4" />} Create release order
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ReleaseOrders() {
  const { user } = useAuth();
  const canManage = ["admin", "supervisor", "general_manager", "depot_manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/release-orders" + (filterStatus ? `?status=${filterStatus}` : ""));
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);
  useEffect(() => { load(); }, [load]);

  const removeOrder = async (rid) => {
    if (!window.confirm("Delete this release order? Only allowed when no slots have been used yet.")) return;
    try {
      await api.delete(`/release-orders/${rid}`);
      toast.success("Release order deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="release-orders-page">
      <PageHeader
        title="Release Orders"
        subtitle="Carrier-issued gate-OUT authorisations — booking number, container types and quantities, gate window."
        right={canManage && (
          <button onClick={() => setCreateOpen(true)} className="ps-btn ps-btn-primary" data-testid="ro-new">
            <Plus className="w-4 h-4" /> New release order
          </button>
        )}
        testid="release-orders-header"
      />
      <div className="p-6 sm:p-8 space-y-4">
        <div className="border border-gray-200 bg-white p-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-semibold text-gray-600">Status:</span>
          {["", "OPEN", "IN_PROGRESS", "COMPLETED", "EXPIRED"].map((s) => (
            <button key={s || "ALL"} onClick={() => setFilterStatus(s)}
              className={`px-3 py-1 border ${filterStatus === s ? "border-[#006E9E] bg-[#006E9E] text-white" : "border-gray-300 hover:bg-gray-50"}`}
              data-testid={`ro-filter-${s || "all"}`}>
              {s || "All"}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border border-gray-200 bg-white p-12 text-center text-sm text-gray-500">
            <PackageOpen className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            No release orders {filterStatus ? `with status ${filterStatus}` : "yet"}.
            {canManage && <div className="mt-2">Click <span className="font-semibold">New release order</span> when the carrier issues one.</div>}
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="border border-gray-200 bg-white" data-testid={`ro-row-${r.id}`}>
                <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
                  <div className="font-mono font-bold text-base">{r.carrier_code}</div>
                  {r.transport_order && (
                    <div className="font-mono text-sm text-gray-700">TO: {r.transport_order}</div>
                  )}
                  <div className={`px-2 py-0.5 text-[10px] uppercase font-bold tracking-[0.16em] border ${STATUS_COLOR[r.status] || ""}`}>{r.status}</div>
                  <div className="text-xs text-gray-600 ml-auto">
                    {r.used_qty}/{r.total_qty} slots used
                    {r.gate_open && <span className="ml-3">· window {(r.gate_open || "").slice(0, 16).replace("T", " ")} → {(r.gate_close || "").slice(0, 16).replace("T", " ")}</span>}
                  </div>
                  {canManage && r.used_qty === 0 && (
                    <button onClick={() => removeOrder(r.id)} className="p-1 text-[#F1471D] hover:bg-red-50" data-testid={`ro-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
                <div className="divide-y divide-gray-100">
                  {r.bookings.map((bk, i) => (
                    <div key={i} className="px-5 py-3">
                      <div className="flex items-center gap-3 text-sm mb-1">
                        <span className="font-mono font-bold text-[#006E9E]">{bk.booking_no}</span>
                        {bk.destination && <span className="text-gray-600">→ {bk.destination}</span>}
                        <span className="text-xs text-gray-500 ml-auto">{bk.used_qty}/{bk.total_qty}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {bk.lines.map((ln, li) => (
                          <span key={li} className={`px-2 py-1 border font-mono ${ln.remaining <= 0 ? "bg-gray-100 text-gray-500 border-gray-200" : "bg-[#F4FBE6] text-[#5BA508] border-[#99D420]"}`}>
                            {ln.used}/{ln.qty} × {ln.iso_type}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)} onSaved={load} />}
    </div>
  );
}
